import usePageMeta from "@/lib/usePageMeta";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import JokeRibbon from "@/components/JokeRibbon";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, FishSimple } from "@phosphor-icons/react";
import SortMenu, { sortProducts } from "@/components/SortMenu";
import { FaqBubble } from "@/components/faq/FaqBubble";

const PAGE = 48;

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const activeCollection = params.get("collection") || "all";
  const [q, setQ] = useState(params.get("q") || "");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collections, setCollections] = useState([]);
  const [allTotal, setAllTotal] = useState(null);
  const [shown, setShown] = useState(PAGE);
  const colName = collections.find((c) => c.id === activeCollection)?.name;
  usePageMeta({
    title: colName ? `${colName} — Funny Fishing Gear` : "Shop Funny Fishing Shirts, Hoodies, Hats & Mugs",
    description: `Browse ${colName ? `${colName.toLowerCase()} and more ` : ""}funny fishing apparel, hats, mugs and home goods from Reel Funny Goods — perfect gifts for anglers.`,
    path: activeCollection === "all" ? "/shop" : `/shop?collection=${activeCollection}`,
    noindex: !!params.get("q"),
  });

  useEffect(() => {
    api.get("/site/collections").then((r) => setCollections(r.data || [])).catch((e) => console.warn("Collections failed to load", e));
  }, []);

  useEffect(() => {
    setLoading(true);
    setShown(PAGE);
    const query = { limit: 500 };
    if (activeCollection !== "all") query.collection = activeCollection;
    if (q) query.q = q;
    const t = setTimeout(() => {
      api
        .get("/products", { params: query })
        .then((r) => {
          setProducts(r.data);
          if (activeCollection === "all" && !q) setAllTotal(r.data.length);
        })
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [activeCollection, q]);

  const tabs = useMemo(() => {
    const base = [{ id: "all", label: "All", count: allTotal ?? undefined }];
    const extras = collections
      .filter((c) => (c.count || 0) > 0)
      .map((c) => ({ id: c.name, label: c.name, count: c.count }));
    return [...base, ...extras];
  }, [allTotal, collections]);

  const sort = params.get("sort") || "featured";
  const sorted = useMemo(() => sortProducts(products, sort), [products, sort]);

  function setSort(v) {
    const next = new URLSearchParams(params);
    if (v === "featured") next.delete("sort");
    else next.set("sort", v);
    setParams(next, { replace: true });
    setShown(PAGE);
  }

  function setCollection(id) {
    const next = new URLSearchParams(params);
    if (id === "all") next.delete("collection");
    else next.set("collection", id);
    next.delete("category");
    next.delete("tag");
    setParams(next);
  }

  return (
    <div data-testid="catalog-page" className="mx-auto max-w-7xl px-6 py-14">
      <FaqBubble />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">The Shop</div>
          <h1 className="font-display text-4xl md:text-6xl mt-2">Every damn design.</h1>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 md:items-center">
          <SortMenu value={sort} onChange={setSort} />
          <div className="relative md:w-80">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search tees, mugs, bait..."
              className="pl-10 h-11 rounded-sm bg-white"
            />
          </div>
        </div>
      </div>

      {tabs.length > 1 && (
        <div data-testid="collection-tabs" className="flex flex-wrap gap-2 mb-10">
          {tabs.map((c) => (
            <button
              key={c.id}
              data-testid={`tab-${c.id}`}
              onClick={() => setCollection(c.id)}
              className={`text-xs uppercase tracking-widest px-4 py-2 rounded-sm border transition-colors inline-flex items-center gap-2 ${
                activeCollection === c.id
                  ? "bg-[#1C211E] text-white border-[#1C211E]"
                  : "bg-white border-border text-foreground/70 hover:border-foreground"
              }`}
            >
              <span>{c.label}</span>
              {c.count != null && (
                <span className={`text-[10px] ${activeCollection === c.id ? "text-white/60" : "text-muted-foreground"}`}>
                  {c.count}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-square bg-muted animate-pulse rounded-sm" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div data-testid="catalog-empty" className="border border-dashed border-border rounded-sm p-16 text-center">
          <FishSimple size={40} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
          <div className="font-display text-xl">Nothing in the net yet</div>
          <p className="text-sm text-muted-foreground mt-2">Try a different filter or clear your search.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {sorted.slice(0, shown).map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}

      {!loading && products.length > shown && (
        <div className="mt-10 flex flex-col items-center gap-2">
          <button
            data-testid="catalog-load-more"
            onClick={() => setShown((n) => n + PAGE)}
            className="h-12 px-8 rounded-sm border border-foreground font-display tracking-wide hover:bg-foreground hover:text-white transition-colors"
          >
            Load more
          </button>
          <span data-testid="catalog-showing" className="text-xs text-muted-foreground">
            Showing {Math.min(shown, products.length)} of {products.length}
          </span>
        </div>
      )}

      <JokeRibbon />
    </div>
  );
}
